import { ArrowLeft, MessageSquare, Share2 } from "lucide-react";
import { MagicalIconButton } from "@/pages/agent-interfaces/live-agent/components/MagicalIconButton";
import { GenieDetailsLink } from "@/pages/agent-interfaces/live-agent/components/GenieDetailsLink";
import { GenieSupportLink } from "@/pages/agent-interfaces/live-agent/components/GenieSupportLink";
import { GenieCallButton } from "@/pages/agent-interfaces/live-agent/components/GenieCallButton";
import { canDialFromDevice } from "@/lib/tel";

/**
 * Back button (admin-only, navigates to the genie hub) + support link + share
 * button, floated top-left/top-right of the default studio layout. Hidden in
 * embed mode, portal mode, and while the full-page conversation canvas owns
 * the chrome (they'd float over the canvas header's own controls). The
 * support link shares the same top-right flex row as the share button
 * (matching LiveAgentPage) instead of its own absolutely-positioned block, so
 * the two never overlap.
 */
export interface StudioHeaderActionsProps {
  isInternalAdmin: boolean;
  isEmbedMode: boolean;
  portalMode: boolean;
  conversationCanvasActive: boolean;
  brand: string;
  secondaryColor?: string;
  helpPageUrl?: string | null;
  /** The Genie's inbound number; phone visitors get a header call button for it. */
  phoneNumber?: string | null;
  agentName?: string | null;
  /** Set during a live conversation while the chat is closed, so it can always be reopened. */
  onShowChat?: () => void;
  /** Set where there is no floating Details tab: on mobile, and on desktop while the chat is on screen. */
  onOpenDetails?: () => void;
  onBack: () => void;
  onShare: () => void;
}

export function StudioHeaderActions({
  isInternalAdmin,
  isEmbedMode,
  portalMode,
  conversationCanvasActive,
  brand,
  secondaryColor,
  helpPageUrl,
  phoneNumber,
  agentName,
  onShowChat,
  onOpenDetails,
  onBack,
  onShare,
}: StudioHeaderActionsProps) {
  return (
    <>
      {/* Back button for admins - hide when embedded or in portal mode. z-[1700]
          keeps it above the welcome gate (z-[1600]) so it's reachable before
          the visitor ever starts talking, not just once a conversation begins. */}
      {isInternalAdmin && !isEmbedMode && !portalMode && !conversationCanvasActive && (
        <div className="absolute top-5 left-5 z-[1700]">
          <MagicalIconButton
            onClick={onBack}
            brandColor={brand}
            secondaryColor={secondaryColor}
            ariaLabel="Back to your Genies"
            hoverHint="Back to your Genies"
            hintAlign="left"
          >
            <ArrowLeft className="w-4 h-4" />
          </MagicalIconButton>
        </div>
      )}

      {/* Support link + share button - top-right, subtle; hide when embedded,
          in portal mode, or while the full-page conversation canvas owns the
          chrome (they would float over the canvas header's controls). Shares
          one flex row so the support link and the share button never overlap. */}
      {!isEmbedMode && !portalMode && !conversationCanvasActive && (
        <div className="absolute top-5 flex items-center gap-2 z-[1700]" style={{ right: "max(20px, env(safe-area-inset-right))" }}>
          <GenieCallButton
            phoneNumber={phoneNumber}
            brandColor={brand}
            secondaryColor={secondaryColor}
            agentName={agentName}
          />
          {onShowChat && (
            <button type="button" onClick={onShowChat} aria-label="Show chat" title="Show chat"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full px-3 text-sm text-gray-700 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary">
              <MessageSquare size={18} aria-hidden="true" />
              <span className="hidden sm:inline">Show chat</span>
            </button>
          )}
          {onOpenDetails && <GenieDetailsLink onClick={onOpenDetails} />}
          {helpPageUrl && <GenieSupportLink href={helpPageUrl} compact={canDialFromDevice(phoneNumber)} />}
          <MagicalIconButton
            onClick={onShare}
            brandColor={brand}
            secondaryColor={secondaryColor}
            ariaLabel="Share live agent"
            hoverHint="Share this Genie with others"
            hintAlign="right"
          >
            <Share2 size={18} strokeWidth={2} />
          </MagicalIconButton>
        </div>
      )}
    </>
  );
}

export default StudioHeaderActions;
